import React, { useState, useEffect, useCallback } from 'react';
import { Button, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { message } from 'antd';
import { makePostCall } from '../../utils/helper';
import { getUserDetails } from '../../utils/helper';
import { fetchDocuments, createDocument } from './documentsApi';
import DocumentsPanel from './DocumentsPanel';
import DocumentEditor from './DocumentEditor';
import TemplatePanel from './TemplatePanel';

const HARDCODED_PACS_ORD_ID = 10290; // TODO: will come from URL params later

export default function OnlyOfficeDocumentsPage() {
  const pacsOrdId = HARDCODED_PACS_ORD_ID;

  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [activeDocId, setActiveDocId] = useState<number | null>(null);
  const [initializing, setInitializing] = useState(true);

  // Fetch order details
  useEffect(() => {
    makePostCall('/orders-list-details', {
      order_ids: [pacsOrdId],
      user_id: getUserDetails()?.username,
    })
      .then((resp) => {
        const data = resp?.data?.data;
        if (data?.length > 0) {
          setOrderDetails(data[0]);
        }
      })
      .catch(() => {
        message.error('Failed to fetch order details');
      });
  }, [pacsOrdId]);

  // Auto-create: check if docs exist, if not create one and open it
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        const res = await fetchDocuments({ pacsOrdId });
        const docs = res.data?.data || [];

        if (cancelled) return;

        if (docs.length === 0) {
          // No documents — auto-create a blank one
          const createRes = await createDocument({
            title: `Report - ${new Date().toLocaleString()}`,
            pacsOrdId,
          });
          if (!cancelled && createRes.data?.data?.id) {
            setActiveDocId(createRes.data.data.id);
          }
        } else {
          // Documents exist — open the most recent one
          setActiveDocId(docs[0].id);
        }
      } catch (err) {
        if (!cancelled) {
          message.error('Failed to initialize documents');
        }
      } finally {
        if (!cancelled) setInitializing(false);
      }
    };

    init();
    return () => { cancelled = true; };
  }, [pacsOrdId]);

  const handleDocumentCreated = useCallback((docId: number) => {
    setActiveDocId(docId);
  }, []);

  if (initializing) {
    return (
      <div className="flex h-full items-center justify-center" style={{ background: '#000' }}>
        <Spin size="large" tip="Loading..." />
      </div>
    );
  }

  const pacs_order = orderDetails?.pacs_order;

  return (
    <div className="flex h-full w-full flex-col" style={{ background: '#000' }}>
      {/* Toolbar */}
      <div className="flex items-center justify-between border-b border-gray-700 px-4 py-2">
        <div className="flex items-center gap-3">
          {activeDocId && (
            <Button
              type="text"
              size="small"
              icon={<ArrowLeftOutlined />}
              onClick={() => setActiveDocId(null)}
              style={{ color: '#ccc' }}
            >
              Documents
            </Button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <TemplatePanel
            pacsOrdId={pacsOrdId}
            modality={pacs_order?.po_modality}
            bodyPart={pacs_order?.po_body_part}
            onDocumentCreated={handleDocumentCreated}
          />
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-hidden">
        {activeDocId ? (
          <DocumentEditor docId={activeDocId} onClose={() => setActiveDocId(null)} />
        ) : (
          <div className="h-full overflow-auto p-4">
            <DocumentsPanel pacsOrdId={pacsOrdId} onOpenDocument={setActiveDocId} />
          </div>
        )}
      </div>
    </div>
  );
}
